import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { AnchorLinkItemProps } from 'antd/es/anchor/Anchor';
import {
  Alert,
  Checkbox,
  Form,
  Input,
  Pagination,
  Select,
  Typography,
  message,
} from 'antd';
import { DownloadOutlined, FileProtectOutlined } from '@ant-design/icons';
import { ActionButton } from '@hdi-seguros/web-components.action-button';

import {
  CardSkeletons,
  ErrorResults,
  Anchor,
  RowSize,
  ColSize,
  InputNumberForm
} from '../../../../../shared/components';
import { PolicyNotFound } from '../../../InsurancePolicyList/components';

import DocumentsService from '../../../../../shared/services/documents';
import ProducersService from '../../../../../shared/services/producers';

import { BOOLEAN_VALUES, Car, DECISION_TYPE, Endorsement, FILE_TYPES, InsurancePolicy, LifePossession, Possessions, Ubication } from '../../../../../shared/interfaces';

import {
  displayErrorMessage,
  displayNotImplementedMessage,
  downloadBase64Files,
  downloadPresignedFile,
  FORMAT_DATE,
  formatNumberAR,
  ValidateForm,
} from '../../../../../shared/utils';
import { _renderCard } from '../../utils/renderSections';

import './index.css';
import { COLORS } from '../../../../../config/themes/colors';
import PossessionsService from '../../../../../shared/services/posessions';
import { EndorsementPolicyDocumentRequest } from '../../../../../shared/interfaces/services/get-endorsement-policy-document-service';
import { DocumentClass } from '../../../../../shared/interfaces/enums/document-class-enum';
import { DocumentType } from '../../../../../shared/interfaces/enums/document-type-enum';
import { GetCertificateWithoutOverdueDebtRequest } from '../../../../../shared/interfaces/services/get-certificate-without-overdue-service';
import dayjs from 'dayjs';
import { getCirculationCardPDF } from '../../utils/circulation-card-document';
import { CirculationCardData } from '../../../../../shared/interfaces/services/circulation-card-pdf-service';
import { Coverage, CoverageConstancyServiceRequest, Insured, Possession, Vehicles } from '../../../../../shared/interfaces/services/coverage-constancy-request';
import { PolicyCreatorDocumentType, PolicyDocumentRequest } from '../../../../../shared/interfaces/services/policy-document-request';
import { AuthContext } from '../../../../../shared/contexts';
import { ARCA_STATUS } from '../../../../../shared/interfaces/enums/arca-status-enum';
import {
  buildInsuredCardColumns,
  getInsuredSelectionKey,
  getInsuredSelectionSummary,
  mapLifePossessionToInsured,
  requiresInsuredSelection,
} from './utils/insured-selection';

interface Props {
  insurancePolicyDetail?: InsurancePolicy,
  possessionsList?: Array<Possessions>;
  endorsement?: Endorsement,
  insuredsByPolicy?: any,
  id: any;
}

const MOCK_SECTIONS: Array<AnchorLinkItemProps> = [
  {
    key: 'solicitar-certificados',
    href: '#solicitar-certificados',
    title: 'Solicitar certificados, constancias y/o póliza digital',
  },
];

export const DocumentsTab: FC<Props> = ({ insurancePolicyDetail, endorsement, possessionsList = [], insuredsByPolicy, id }) => {
  const [allPersons, setAllPersons] = useState<LifePossession[]>([]);
  const [selectedInsuredKeys, setSelectedInsuredKeys] = useState<string[]>([]);
  const [informationInsured, setInformationInsured] = useState<any>();
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listDocument, setListDocument] = useState<any>();
  const [optionSelected, setOptionSelected] = useState<string>('');
  const [insuredListPage, setInsuredListPage] = useState(1);
  const [insuredListPageSize, setInsuredListPageSize] = useState(10);
  const [formClause] = Form.useForm();
  const [showError, setShowError] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [showARCAAlert, setShowARCAAlert] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [document, setDocument] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(0);
  const validateForm = new ValidateForm();
  const auth = useContext(AuthContext);

  const insuredSelect = useMemo(
    () =>
      allPersons
        .filter((person) =>
          selectedInsuredKeys.includes(getInsuredSelectionKey(person))
        )
        .map((person) => mapLifePossessionToInsured(person, endorsement)),
    [allPersons, selectedInsuredKeys, endorsement]
  );

  const insuredSelectionSummary = useMemo(
    () => getInsuredSelectionSummary(allPersons.length, selectedInsuredKeys, allPersons),
    [allPersons, selectedInsuredKeys]
  );

  const handleToggleInsured = useCallback((person: LifePossession, checked: boolean) => {
    const personKey = getInsuredSelectionKey(person);

    setSelectedInsuredKeys((previousKeys) => {
      if (checked) {
        return previousKeys.includes(personKey)
          ? previousKeys
          : [...previousKeys, personKey];
      }

      return previousKeys.filter((key) => key !== personKey);
    });
  }, []);

  const handleSelectAllChange = useCallback((checked: boolean) => {
    setSelectedInsuredKeys(
      checked ? allPersons.map(getInsuredSelectionKey) : []
    );
  }, [allPersons]);

  const handleInsuredListPageChange = useCallback((page: number, pageSize: number) => {
    setInsuredListPage(page);
    setInsuredListPageSize(pageSize);
    setIsLoading(false);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const insuredCards = useMemo(
    () =>
      allPersons.map((person) => {
        const personKey = getInsuredSelectionKey(person);
        const isSelected = selectedInsuredKeys.includes(personKey);

        return buildInsuredCardColumns(
          person,
          <Checkbox
            checked={isSelected}
            onChange={({ target: { checked } }) =>
              handleToggleInsured(person, checked)
            }
          />
        );
      }),
    [allPersons, selectedInsuredKeys, handleToggleInsured]
  );

  const paginatedInsuredCards = useMemo(() => {
    const startIndex = (insuredListPage - 1) * insuredListPageSize;
    return insuredCards.slice(startIndex, startIndex + insuredListPageSize);
  }, [insuredCards, insuredListPage, insuredListPageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(allPersons.length / insuredListPageSize));
    if (insuredListPage > maxPage) {
      setInsuredListPage(maxPage);
    }
  }, [allPersons.length, insuredListPage, insuredListPageSize]);
  
  useEffect(() => {
    getDetail();
    /* eslint-disable-next-line */
  }, []);

  const getDetail = async () => {
    formClause.setFieldsValue({ clause: null });
    formClause.setFieldsValue({ beneficiaryClause: null });

    try {
      await Promise.all([getListDocumentSelect(), getDetailInsured()]);
      setShowError(false);
    } catch (error) {
      setShowError(true);
    }
  };

  const getListDocumentSelect = async () => {
    try {
      const branch = insurancePolicyDetail!.rama;
      const articule = insurancePolicyDetail!.articulo;
      const response = await ProducersService?.getListDocument(
        branch,
        articule
      );

      const filteredDocuments = Array.isArray(response)
        ? response.filter((doc: any) => {
            const isCuponeraDePago = doc.value === DocumentType.CuponeraDePago.toString();
            const isEfectivo = insurancePolicyDetail?.formaDePago?.tipoDePago.efectivo === DECISION_TYPE.YES;
            
            if (isCuponeraDePago && !isEfectivo) {
              return false;
            }
            return true;
          })
        : response;

      setListDocument(filteredDocuments);
    } catch (error) { }
  };

  const getDetailInsured = async () => {
    try {
      const branch = insurancePolicyDetail?.rama;
      const response = await ProducersService.getInsureds(branch, id);

      setInformationInsured(response);
    } catch (error) { }
  };

  const handleChangeQuanity = (quantity: any) => {
    setQuantity(quantity);
  }

  const getListInsured = async () => {
    setIsLoading(true);
    setSelectedInsuredKeys([]);
    setInsuredListPage(1);

    try {
      const branch = insurancePolicyDetail!.rama;
      const articule = insurancePolicyDetail!.articulo;
      const policy = id;
      const superPolicy = insurancePolicyDetail!.superpoliza;

      const response = await PossessionsService.getLifePossessions({
        rama: branch,
        poliza: policy,
        articulo: articule,
        superpoliza: superPolicy
      });

      const persons = response.possessions as Array<LifePossession>;

      setAllPersons(persons);
      setIsEmpty(persons.length === 0);
    } catch (error) {
      setAllPersons([]);
      setIsEmpty(true);
    } finally {
      setIsLoading(false);
    }
  };

  const validateDownloadPDFCoverage = (documentTypeValue: any, showDownloadButton: boolean, invokeGetListInsured: boolean) => {
    if (invokeGetListInsured) {
      getListInsured();
    } else {
      setAllPersons([]);
      setIsEmpty(false);
    }

    setShowDownloadButton(showDownloadButton);
    setDocument(documentTypeValue);
  }

  //VALIDATE STATUS ARCA FOR POLICE DOCUMENTS
  const validateDownloadPoliceDocuments = (documentTypeValue: any) => {
    if(insurancePolicyDetail?.statusArca === ARCA_STATUS.PENDING_APPROVAL || insurancePolicyDetail?.statusArca === ARCA_STATUS.REJECTED){
      setShowDownloadButton(false);
      setShowARCAAlert(true);
    } else {
      setAllPersons([]);
      setShowDownloadButton(true);
      setShowARCAAlert(false);
      setDocument(documentTypeValue);
    }
  }

  const policeDocuments = [
    DocumentType.PolizaDigitalCompleta.toString(),
    DocumentType.PolizaDigitalReducida.toString(),
    DocumentType.FacturaOrganizador.toString(),
    DocumentType.CuponeraDePago.toString(),
    DocumentType.FacturaAsegurado.toString(),
    DocumentType.FacturaProductor.toString(),
    DocumentType.TarjetaCirculacion.toString(),];

  const handleOnChange = (value: string) => {

    const otherDocuments = [
      DocumentType.CuadernilloDeAsistencia.toString(),
    ];

    if(DocumentType.ConstanciaCoberturaAPSinClausula.toString() === value || DocumentType.ConstanciaCoberturaAPConClausula.toString())
    {
      getListInsured();
      setDocument(value);
    }  

    if (DocumentType.ConstanciaCoberturaAPBeneficiarioPrimerTermino.toString() === value) {
      validateDownloadPDFCoverage(value, false, true);
    } else if (DocumentType.CertificadoCoberturaCalderas === value
      && possessionsList.filter((possesion: Possessions) =>
        ((possesion as Ubication).coberturas && (possesion as Ubication).coberturas.some(cobertura => cobertura.isCalderas === DECISION_TYPE.YES))).length > 0) {
      validateDownloadPDFCoverage(value, true, false);
    } else if (DocumentType.CertificadoCoberturaAscensores === value
      && possessionsList.filter((possesion: Possessions) =>
        ((possesion as Ubication).coberturas && (possesion as Ubication).coberturas.some(cobertura => cobertura.isAscensores === DECISION_TYPE.YES))).length > 0) {
      validateDownloadPDFCoverage(value, true, false);
    } else if (DocumentType.ConstanciaCoberturaVehiculos === value ){
      validateDownloadPDFCoverage(value, true, false);
    }  else if (DocumentType.CertificadoCoberturaRcCarteles === value
      && possessionsList.filter((possesion: Possessions) =>
        ((possesion as Ubication).coberturas && (possesion as Ubication).coberturas.some(cobertura => cobertura.isCarteles === DECISION_TYPE.YES))).length > 0) {
      validateDownloadPDFCoverage(value, true, false);
    } else if (DocumentType.ConstanciaCoberturaConsorcio === value) {
      validateDownloadPDFCoverage(value, true, false);
    } else if (DocumentType.ConstanciaHogarCombinadoFamiliar === value) {
      validateDownloadPDFCoverage(value, true, false);
    } else if (DocumentType.ConstanciaCoberturaComercio === value) {
      validateDownloadPDFCoverage(value, true, false);
    } else if (
      value === DocumentType.ConstanciaCoberturaVidaColectivo
      || value === DocumentType.ConstanciaCoberturaVidaSaldoDeudor
      || value === DocumentType.ConstanciaCoberturaSepelio
      || value === DocumentType.ConstanciaCoberturaVidaObligatorioCCVOB
      || value === DocumentType.ConstanciaCoberturaVidaObligatorioVIDOB
    ) {
      validateDownloadPDFCoverage(value, false, true);
    } else if (DocumentType.CertificadoDePolizaSinDeudaVencida.toString() === value) {
      if (insurancePolicyDetail?.isAnulada === DECISION_TYPE.YES) {
        displayErrorMessage('No se permite obtener este documento para una póliza anulada.');
        setAllPersons([]);
        setShowDownloadButton(false);
        setShowARCAAlert(false);
      } else if (insurancePolicyDetail?.permiteCertificadoSinDeuda !== DECISION_TYPE.YES) {
        displayErrorMessage('No se permite obtener este documento para una póliza con deuda vencida impaga.');
        setAllPersons([]);
        setShowDownloadButton(false);
        setShowARCAAlert(false);
      } else {
        setAllPersons([]);
        setShowDownloadButton(true);
        setShowARCAAlert(false);
        setDocument(value);
      }
    }else if(otherDocuments.includes(value)){
      setAllPersons([]);
      setShowDownloadButton(true);
      setDocument(value); 
    }else if(policeDocuments.includes(value)){
      validateDownloadPoliceDocuments(value);
    }else{
      setAllPersons([]);
      setShowDownloadButton(false);
      setShowARCAAlert(false);
    }

    setSelectedInsuredKeys([]);
    setInsuredListPage(1);
    setOptionSelected(value);
  };

  const requestDocument = async () => {

    if (insuredSelect.length === 0) {
      const messageWarning = 'No se ha seleccionado ningún asegurado.';
      message.warning(messageWarning);

      return;
    }

  // * VIDA COLECTIVO * //
  if(document === DocumentType.ConstanciaCoberturaVidaColectivo
    || document === DocumentType.ConstanciaCoberturaVidaSaldoDeudor
    || document === DocumentType.ConstanciaCoberturaSepelio
    || document === DocumentType.ConstanciaCoberturaVidaObligatorioCCVOB
    || document === DocumentType.ConstanciaCoberturaVidaObligatorioVIDOB
  ) {
    try {

      const ARTICULO_LEYENDAS: Record<string, string> = {
        '80': 'Vida Colectivo',
        '84': 'Vida Saldo Deudor',
        '85': 'Sepelio',
        '86': 'Vida Colectivo Trabajadores Rurales Ley 16.600',
        '89': 'Vida Obligatorio Decreto 1567/74',
      };           
      const leyendaRama: string = ARTICULO_LEYENDAS[insurancePolicyDetail?.rama ?? ''] ?? '---';

      const insuredSelectList: Insured[] = insuredSelect
                  .map((insuredSelect: { coberturas: any; integrante: any; documentoTipo: any; documentoNumero: any; numeroCuil: string; fechaEgreso: string}) => {
          const insuredSelected: Insured = {
            nombreApellido: insuredSelect.integrante,
            tipoDocumento: insuredSelect.documentoNumero && insuredSelect.documentoNumero.trim() !== "" ? insuredSelect.documentoTipo : 'CUIL',
            numeroDocumento: insuredSelect.documentoNumero && insuredSelect.documentoNumero.trim() !== "" ?  insuredSelect.documentoNumero :  insuredSelect.numeroCuil,
            coberturas: insuredSelect.coberturas
          };
          return insuredSelected;
        });  

      const request: CoverageConstancyServiceRequest = {
        rama: leyendaRama,
        poliza: id,
        vigenciaDesde: dayjs(insurancePolicyDetail!.fechaVigDesd).format(FORMAT_DATE.AR),
        vigenciaHasta: dayjs(insurancePolicyDetail!.fechaVigHast).format(FORMAT_DATE.AR),
        fechaHoraReporte: dayjs().format(FORMAT_DATE.AR_HOUR_MIN),
        tomador: insurancePolicyDetail!.nombreAsegurado,
        domicilio: getValueByLabel(insuredsByPolicy.address, "Domicilio") ?? '',
        productor: insurancePolicyDetail!.nombreProductor,
        productorCodigo: insurancePolicyDetail!.codigoProductor,
        tieneClausulaNoRepeticion: BOOLEAN_VALUES.FALSE,
        asegurados: insuredSelectList
      }

      const filename = `ConstanciaCoberturaVida_${insurancePolicyDetail?.nroPoliza}.pdf`;  
      const response = await DocumentsService.getCollectiveLifeCoverageDocument(request);
      downloadBase64Files(response.data.base64, filename, FILE_TYPES.PDF); 
    } catch (error) {}
    
    // ** ACCIDENTES PERSONALES ** /
    } else {
      
      try {
        await formClause.validateFields();
  
        const { clause } = await formClause.getFieldsValue();
        const { beneficiaryClause } = await formClause.getFieldsValue();
        const isApConClausula = DocumentType.ConstanciaCoberturaAPConClausula.toString() === document;
        const isPAWithFirstBeneficiaryClause = DocumentType.ConstanciaCoberturaAPBeneficiarioPrimerTermino.toString() === document;
        let response;
        
        const insuredSelectListLifeConstancy: Insured[] = insuredSelect.map((insuredSelect: { coberturas: any; integrante: any; documentoTipo: any; documentoNumero: any; numeroCuil: string; descripcionActividad: any; fechaEgreso: string}) => {          
           const insuredSelected: Insured = {
             nombreApellido: insuredSelect.integrante,
             tipoDocumento: insuredSelect.documentoNumero && insuredSelect.documentoNumero.trim() !== "" ? insuredSelect.documentoTipo : 'CUIL',
             numeroDocumento: insuredSelect.documentoNumero && insuredSelect.documentoNumero.trim() !== "" ?  insuredSelect.documentoNumero :  insuredSelect.numeroCuil,
             coberturas: insuredSelect.coberturas,
             descripcionActividad: insuredSelect.descripcionActividad,
           };
           return insuredSelected;
         }); 

         let hasAClause: string = BOOLEAN_VALUES.FALSE.toString();
         if (typeof clause === "string" && clause.trim() !== "") {
           hasAClause = BOOLEAN_VALUES.TRUE.toString();
         }

         let hasABeneficiaryClause: string = BOOLEAN_VALUES.FALSE.toString();
         if (typeof beneficiaryClause === "string" && beneficiaryClause.trim() !== "") {
           hasABeneficiaryClause = BOOLEAN_VALUES.TRUE.toString();
         }
  
        const beneficiarioTomador = insurancePolicyDetail?.beneficiariosPoliza?.find(
          (beneficiario) => beneficiario.isTomador === DECISION_TYPE.YES
        );
        const nombreTomador = beneficiarioTomador?.nombre || insurancePolicyDetail!.nombreAsegurado;
  
        if (isApConClausula) {

          const request: CoverageConstancyServiceRequest = {
            rama: insurancePolicyDetail!.descripcionRama,
            poliza: id,
            vigenciaDesde: dayjs(insurancePolicyDetail!.fechaVigDesd).format(FORMAT_DATE.AR),
            vigenciaHasta: dayjs(insurancePolicyDetail!.fechaVigHast).format(FORMAT_DATE.AR),
            fechaHoraReporte: dayjs().format(FORMAT_DATE.AR_HOUR_MIN),
            tomador: nombreTomador,
            domicilio: informationInsured?.address[0]?.value,
            productor: insurancePolicyDetail!.nombreProductor,
            productorCodigo: insurancePolicyDetail!.codigoProductor,
            tieneClausulaNoRepeticion: hasAClause,
            clausulaNoRepeticion: hasAClause===BOOLEAN_VALUES.TRUE.toString() ? clause : '',
            nombreLeyendaBeneficiario: insurancePolicyDetail!.nombreAsegurado,
            asegurados: insuredSelectListLifeConstancy
          }

          response = await DocumentsService.getApConstancyWithClauseInPDF(request);

        } else if (isPAWithFirstBeneficiaryClause) {

          const request: CoverageConstancyServiceRequest = {
            rama: insurancePolicyDetail!.descripcionRama,
            poliza: id,
            vigenciaDesde: dayjs(insurancePolicyDetail!.fechaVigDesd).format(FORMAT_DATE.AR),
            vigenciaHasta: dayjs(insurancePolicyDetail!.fechaVigHast).format(FORMAT_DATE.AR),
            fechaHoraReporte: dayjs().format(FORMAT_DATE.AR_HOUR_MIN),
            tomador: nombreTomador,
            domicilio: informationInsured?.address[0]?.value,
            productor: insurancePolicyDetail!.nombreProductor,
            productorCodigo: insurancePolicyDetail!.codigoProductor,
            tieneClausulaNoRepeticion: hasAClause,
            tieneBeneficiarioPrimerTermino: hasABeneficiaryClause,
            clausulaNoRepeticion: hasAClause===BOOLEAN_VALUES.TRUE.toString() ? clause : '',
            nombreLeyendaBeneficiario: (beneficiaryClause === null || beneficiaryClause === undefined || beneficiaryClause === "")
                ? ""
                : beneficiaryClause,
            asegurados: insuredSelectListLifeConstancy
          }
  
          response = await DocumentsService.getApWithPrimaryBeneficiaryClausePDF(request);
          
        }else {
          const request: CoverageConstancyServiceRequest = {
            rama: insurancePolicyDetail!.descripcionRama,
            poliza: id,
            vigenciaDesde: dayjs(insurancePolicyDetail!.fechaVigDesd).format(FORMAT_DATE.AR),
            vigenciaHasta: dayjs(insurancePolicyDetail!.fechaVigHast).format(FORMAT_DATE.AR),
            fechaHoraReporte: dayjs().format(FORMAT_DATE.AR_HOUR_MIN),
            tomador: nombreTomador,
            domicilio: informationInsured?.address[0]?.value,
            productor: insurancePolicyDetail!.nombreProductor,
            productorCodigo: insurancePolicyDetail!.codigoProductor,
            tieneClausulaNoRepeticion: hasAClause,
            nombreLeyendaBeneficiario: insurancePolicyDetail!.nombreAsegurado,
            asegurados: insuredSelectListLifeConstancy
          }
  
          response = await DocumentsService.getApConstancyWithoutClauseInPDF(request);
        }
  
        const base64 = response?.data?.base64;
        const fileName = isPAWithFirstBeneficiaryClause ? `ConstanciaAP_Beneficiario_en_primer_termino_${id}` :`ConstanciaAP${isApConClausula ? 'Con' : 'Sin'}Clausula_${id}`;
  
        downloadBase64Files(base64, fileName, FILE_TYPES.PDF);
  
      } catch (error) { }
    }
  };

  function getValueByLabel(section: any[], label: string): string | undefined {
    return section.find(item => item.label === label)?.value;
  }

  const downloadEndorsementPolicyPDF = async () => {
    try {
      let documentToDownload = '';
      setDownloading(true);
      switch (document) {
        case DocumentType.PolizaDigitalCompleta:
          documentToDownload = endorsement?.documentoPdf ?? '';
          break;
        case DocumentType.PolizaDigitalReducida:
          documentToDownload = endorsement?.pdfReducido ?? '';
          break;
        case DocumentType.CuponeraDePago:
          documentToDownload = endorsement?.chequeraDePago ?? '';
          break;
        case DocumentType.FacturaAsegurado:
          documentToDownload = endorsement?.facturaAsegurado ?? '';
          break;
        case DocumentType.FacturaOrganizador:
          documentToDownload = endorsement?.facturaOrganizador ?? '';
          break;
        case DocumentType.FacturaProductor:
          documentToDownload = endorsement?.facturaProductor ?? '';
          break;
        case DocumentType.CuadernilloDeAsistencia:
          documentToDownload = endorsement?.cuadernilloAsistencia ?? '';
          break;
        case DocumentType.CertificadoDePolizaSinDeudaVencida:
          documentToDownload = `CertificadoSinDeuda_${insurancePolicyDetail?.descripcionRama}_${insurancePolicyDetail?.nroPoliza}.pdf`;
          break;
        case DocumentType.CertificadoCoberturaRcCarteles:
          documentToDownload = `CertificadoCoberturaRcCarteles_${insurancePolicyDetail?.descripcionRama}_${insurancePolicyDetail?.nroPoliza}.pdf`;
          break;
        case DocumentType.CertificadoCoberturaCalderas:
          documentToDownload = `CertificadoCoberturaRcCalderas_${insurancePolicyDetail?.descripcionRama}_${insurancePolicyDetail?.nroPoliza}.pdf`;
          break;
        case DocumentType.CertificadoCoberturaAscensores:
          documentToDownload = `CertificadoCoberturaRcAscensores_${insurancePolicyDetail?.descripcionRama}_${insurancePolicyDetail?.nroPoliza}.pdf`;
          break;
        case DocumentType.ConstanciaCoberturaConsorcio:
          documentToDownload = `ConstanciaCoberturaConsorcio_${insurancePolicyDetail?.descripcionRama}_${insurancePolicyDetail?.nroPoliza}.pdf`;
          break;
        case DocumentType.ConstanciaHogarCombinadoFamiliar:
          documentToDownload = `ConstanciaHogarCombinadoFamiliar_${insurancePolicyDetail?.descripcionRama}_${insurancePolicyDetail?.nroPoliza}.pdf`;
          break;
        case DocumentType.ConstanciaCoberturaComercio:     
          documentToDownload = `ConstanciaCoberturaComercio_${insurancePolicyDetail?.nroPoliza}.pdf`;
          break;
        case DocumentType.ConstanciaCoberturaVehiculos:     
          documentToDownload = `ConstanciaCoberturaVehiculo_${insurancePolicyDetail?.nroPoliza}.pdf`;
          break;  
        case DocumentType.TarjetaCirculacion:
          documentToDownload = endorsement?.tarjetaCirculacion ?? '';
          break;      
        default:
          displayNotImplementedMessage();
      }
      if (documentToDownload && !(documentToDownload.length === 0)) {         
        if (document === DocumentType.CertificadoDePolizaSinDeudaVencida) {          
          const request: GetCertificateWithoutOverdueDebtRequest = {
            tipoDocumento: insurancePolicyDetail?.tipoDeDeuda!,
            nroPoliza: insurancePolicyDetail?.nroPoliza!,
            descripcionRama: insurancePolicyDetail?.descripcionRama!,
            nombreAsegurado: insurancePolicyDetail?.nombreAsegurado!,
            fechaVigDesd: dayjs(insurancePolicyDetail?.fechaVigDesd!).format(FORMAT_DATE.AR),
            fechaVigHast: dayjs(insurancePolicyDetail?.fechaVigHast!).format(FORMAT_DATE.AR),
            nombreBanco: insurancePolicyDetail?.formaDePago.nombreBanco || '',
            nombreTarjeta: insurancePolicyDetail?.formaDePago.tarjeta.nombreTarjeta || '',
            codigoFormaPago: insurancePolicyDetail?.formaDePago!.tipoDePago.codigo!,
            codigoDeRama: insurancePolicyDetail?.rama!,
            articulo: insurancePolicyDetail?.articulo!,
            superpoliza: insurancePolicyDetail?.superpoliza!,
          }
          const response = await DocumentsService.getCertificateOfOverdueDebt(request);

          downloadBase64Files(response.data.base64, documentToDownload, FILE_TYPES.PDF);
        } else if (document === DocumentType.CertificadoCoberturaRcCarteles) {
          try {
            const possessions : Ubication[] = possessionsList.filter((possession : Possessions) : possession is Ubication =>
              (possession as Ubication).coberturas && (possession as Ubication).coberturas.some(cobertura => cobertura.isCarteles === DECISION_TYPE.YES));

            const coverages = possessionsList.flatMap((possession : Possessions) => 
              (possession as Ubication).coberturas?
              (possession as Ubication).coberturas.filter(cobertura => cobertura.isCarteles === DECISION_TYPE.YES) : []);
            const totalCoverages = coverages.reduce((acc, coverage) => {
              return acc + (Number(coverage.sumaCob) || 0);
            },0);

            const possessionsAux: Possession[] = possessions.map(possession => {
              const risksCoverage: Coverage[] = [{
                coberturaNombre: 'RC CARTELES',
                moneda: endorsement?.moneda ?? '$',
                sumaAsegurada: isNaN(totalCoverages) ? '0,00' : formatNumberAR(String(totalCoverages)),
              }];

              const possesionAux: Possession = {
                ubicacion: possessions[0]?.direccion,
                localidadBien: possessions[0]?.localidad,
                codigoPostalBien: possessions[0]?.codPostal,
                ocupacion: possessions[0]?.ocupacion,
                actividad: possessions[0]?.actividadComercial ?? '',
                clausulas: possessions[0]?.clausulas.map(c => c.clausula).join(" - ") ?? '',
                coberturas: risksCoverage,                 
              };
              return possesionAux;
            });

            const request: CoverageConstancyServiceRequest = {
              rama: insurancePolicyDetail?.descripcionRama ?? '',
              poliza: insurancePolicyDetail?.nroPoliza!,
              vigenciaDesde: dayjs(insurancePolicyDetail!.fechaVigDesd!).format(FORMAT_DATE.AR),
              vigenciaHasta: dayjs(insurancePolicyDetail!.fechaVigHast).format(FORMAT_DATE.AR),
              fechaHoraReporte: dayjs().format(FORMAT_DATE.AR_HOUR_MIN),
              asegurado: insurancePolicyDetail?.nombreAsegurado ?? '',
              domicilio: (getValueByLabel(insuredsByPolicy.address, "Domicilio") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Localidad") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Provincia") ?? ''),
              productor: insurancePolicyDetail!.nombreProductor,
              productorCodigo: insurancePolicyDetail!.codigoProductor,
              endoso: endorsement?.nro,
              bienes: possessionsAux,
            }

            const response = await DocumentsService.getCertificateOfProofCivilLiabilityCoveragePosters(request);
            downloadBase64Files(response.data.base64, documentToDownload, FILE_TYPES.PDF);
            
          } catch (error) {
          }

        } else if (document === DocumentType.CertificadoCoberturaCalderas) {
          try {
            
            const possessions : Ubication[] = possessionsList.filter((possession : Possessions) : possession is Ubication =>
              (possession as Ubication).coberturas && (possession as Ubication).coberturas.some(cobertura => cobertura.isCalderas === DECISION_TYPE.YES));

            const coverages = possessionsList.flatMap((possession : Possessions) => 
              (possession as Ubication).coberturas?
              (possession as Ubication).coberturas.filter(cobertura => cobertura.isCalderas === DECISION_TYPE.YES) : []);
            const totalCoverages = coverages.reduce((acc, coverage) => {
              return acc + (Number(coverage.sumaCob) || 0);
            },0);

            const possessionsAux: Possession[] = possessions.map(possession => {
              const risksCoverage: Coverage[] = [{
                coberturaNombre: coverages[0]?.nombreCob +' ('+quantity+' CALDERAS)',
                moneda: endorsement?.moneda ?? '$',
                sumaAsegurada: isNaN(totalCoverages) ? '0,00' : formatNumberAR(String(totalCoverages)),
              }];

              const possesionAux: Possession = {
                ubicacion: possessions[0]?.direccion +' ('+ possessions[0]?.codPostal +'), '+possessions[0]?.localidad,
                localidadBien: possessions[0]?.localidad,
                codigoPostalBien: possessions[0]?.codPostal,
                riesgoCubierto: possessions[0]?.riesgo,
                observaciones: coverages[0]?.resolucionCalderas,
                coberturas: risksCoverage,                 
              };
              return possesionAux;
            });

            const request: CoverageConstancyServiceRequest = {
              rama: insurancePolicyDetail?.descripcionRama ?? '',
              poliza: insurancePolicyDetail?.nroPoliza!,
              vigenciaDesde: dayjs(insurancePolicyDetail!.fechaVigDesd!).format(FORMAT_DATE.AR),
              vigenciaHasta: dayjs(insurancePolicyDetail!.fechaVigHast).format(FORMAT_DATE.AR),
              fechaHoraReporte: dayjs().format(FORMAT_DATE.AR_HOUR_MIN),
              asegurado: insurancePolicyDetail?.nombreAsegurado ?? '',
              domicilio: (getValueByLabel(insuredsByPolicy.address, "Domicilio") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Localidad") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Provincia") ?? ''),
              productor: insurancePolicyDetail!.nombreProductor,
              productorCodigo: insurancePolicyDetail!.codigoProductor,
              bienes: possessionsAux,
            }

            const response = await DocumentsService.getBoilerCoverageDocument(request);
            downloadBase64Files(response.data.base64, documentToDownload, FILE_TYPES.PDF);
            
          } catch (error) {
          }
        
        } else if (document === DocumentType.CertificadoCoberturaAscensores) {
          try {
            const possessions : Ubication[] = possessionsList.filter((possession : Possessions) : possession is Ubication =>
              (possession as Ubication).coberturas && (possession as Ubication).coberturas.some(cobertura => cobertura.isAscensores === DECISION_TYPE.YES));
            const coverages = possessionsList.flatMap((possession : Possessions) => 
              (possession as Ubication).coberturas?
              (possession as Ubication).coberturas.filter(cobertura => cobertura.isAscensores === DECISION_TYPE.YES) : []);
            const totalCoverages = coverages.reduce((acc, coverage) => {
              return acc + (Number(coverage.sumaCob) || 0);
            },0);

            const possessionsAux: Possession[] = possessions.map(possession => {
              const risksCoverage: Coverage[] = [{
                coberturaNombre: coverages[0]?.nombreCob +' ('+quantity+' ASCENSORES)',
                moneda: endorsement?.moneda ?? '$',
                sumaAsegurada: isNaN(totalCoverages) ? '0,00' : formatNumberAR(String(totalCoverages)),
              }];

              const possesionAux: Possession = {
                ubicacion: possessions[0]?.direccion +' ('+ possessions[0]?.codPostal +'), '+possessions[0]?.localidad,
                localidadBien: possessions[0]?.localidad,
                codigoPostalBien: possessions[0]?.codPostal,
                riesgoCubierto: possessions[0]?.riesgo,
                observaciones: coverages[0]?.resolucionAscensores,
                coberturas: risksCoverage,                 
              };
              return possesionAux;
            });

            const request: CoverageConstancyServiceRequest = {
              rama: insurancePolicyDetail?.descripcionRama ?? '',
              poliza: insurancePolicyDetail?.nroPoliza!,
              vigenciaDesde: dayjs(insurancePolicyDetail!.fechaVigDesd!).format(FORMAT_DATE.AR),
              vigenciaHasta: dayjs(insurancePolicyDetail!.fechaVigHast).format(FORMAT_DATE.AR),
              fechaHoraReporte: dayjs().format(FORMAT_DATE.AR_HOUR_MIN),
              asegurado: insurancePolicyDetail?.nombreAsegurado ?? '',
              domicilio: (getValueByLabel(insuredsByPolicy.address, "Domicilio") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Localidad") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Provincia") ?? ''),
              productor: insurancePolicyDetail!.nombreProductor,
              productorCodigo: insurancePolicyDetail!.codigoProductor,
              bienes: possessionsAux,
            }

            const response = await DocumentsService.getBoilerCoverageDocument(request);
            downloadBase64Files(response.data.base64, documentToDownload, FILE_TYPES.PDF);
            
          } catch (error) {
          }
        
        } else if (document === DocumentType.ConstanciaCoberturaConsorcio) {
          try {
            const possessions: Ubication[] = possessionsList.filter(
              (possession: Possessions): possession is Ubication =>
                (possession as Ubication).coberturas && (possession as Ubication).coberturas !== undefined
            );

            const possessionsAux: Possession[] = possessions.map(possession => {
              const risksCoverage: Coverage[] = (possession.coberturas ?? []).map(coverage => ({
                coberturaNombre: coverage.nombreCob,
                moneda: endorsement?.moneda ?? '$',
                sumaAsegurada: formatNumberAR(coverage.sumaCob),
              }));

              const possesionAux: Possession = {
                ubicacion: possession.direccion,
                localidadBien: possession.localidad,
                codigoPostalBien: possession.codPostal,
                ocupacion: possession.ocupacion,
                clausulas: possession.clausulas.map(c => c.clausula).join(" - ") ?? '',
                coberturas: risksCoverage,                 
              };
              return possesionAux;
            });

            const request: CoverageConstancyServiceRequest = {
              rama: insurancePolicyDetail?.descripcionRama ?? '',
              poliza: insurancePolicyDetail?.nroPoliza!,
              vigenciaDesde: dayjs(insurancePolicyDetail!.fechaVigDesd!).format(FORMAT_DATE.AR),
              vigenciaHasta: dayjs(insurancePolicyDetail!.fechaVigHast).format(FORMAT_DATE.AR),
              fechaHoraReporte: dayjs().format(FORMAT_DATE.AR_HOUR_MIN),
              asegurado: insurancePolicyDetail?.nombreAsegurado ?? '',
              domicilio: (getValueByLabel(insuredsByPolicy.address, "Domicilio") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Localidad") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Provincia") ?? ''),
              productor: insurancePolicyDetail!.nombreProductor,
              productorCodigo: insurancePolicyDetail!.codigoProductor,
              endoso: endorsement?.nro,
              bienes: possessionsAux,
            }

            const response = await DocumentsService.getConsortiumCoverageDocument(request);
            downloadBase64Files(response.data.base64, documentToDownload, FILE_TYPES.PDF);
          } catch (error) {
          }
          
        } else if (document === DocumentType.ConstanciaCoberturaVehiculos) {
          try {
             const possessions: Car[] = possessionsList.filter(
              (possession) : possession is Car => 'patente' in possession
             );

            let vehicles : Vehicles[] = [];
            possessions.forEach(car => {
              const vehicle: Vehicles = {
                marca: car.marca,
                modelo: car.modelo,
                anio: car.anio,
                tipo: car.tipo,
                uso: car.uso,
                motor: car.motor,
                chasis: car.chasis,
                carroceria: car.carroceria,
                patente: car.patente,
                valorAsegurado: formatNumberAR(car.sumaAsegurada),
                cobertura: car.cobertura,
                franquicia: formatNumberAR(car.importeFranquiciaDanios) ?? '0,00',
                acreedorPrendario: car.AcreedorPrendario,
                moneda: endorsement!.moneda,
                descripcionDeCobertura: car.descripcionCobAuto,
              }
              vehicles.push(vehicle);
            })

            const request: CoverageConstancyServiceRequest = {
              rama: insurancePolicyDetail?.descripcionRama ?? '',
              poliza: insurancePolicyDetail?.nroPoliza!,
              vigenciaDesde: dayjs(insurancePolicyDetail!.fechaVigDesd!).format(FORMAT_DATE.AR),
              vigenciaHasta: dayjs(insurancePolicyDetail!.fechaVigHast).format(FORMAT_DATE.AR),
              fechaHoraReporte: dayjs().format(FORMAT_DATE.AR_HOUR_MIN),
              asegurado: insurancePolicyDetail?.nombreAsegurado ?? '',
              domicilio: (getValueByLabel(insuredsByPolicy.address, "Domicilio") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Localidad") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Provincia") ?? ''),
              productor: insurancePolicyDetail!.nombreProductor,
              productorCodigo: insurancePolicyDetail!.codigoProductor,
              endoso: endorsement?.nro,
              bienes: vehicles,
            }

            const response = await DocumentsService.getDocumentCarCoverage(request);
            downloadBase64Files(response.data.base64, documentToDownload, FILE_TYPES.PDF);
          } catch (error) {
          }
        }  else if (document === DocumentType.ConstanciaHogarCombinadoFamiliar) {
          try {
            const possessions: Ubication[] = possessionsList.filter(
              (possession: Possessions): possession is Ubication =>
                (possession as Ubication).coberturas && (possession as Ubication).coberturas !== undefined
            );

            const possessionsAux: Possession[] = possessions.map(possession => {
              const risksCoverage: Coverage[] = (possession.coberturas ?? []).map(coverage => ({
                coberturaNombre: coverage.nombreCob,
                moneda: endorsement?.moneda ?? '$',
                sumaAsegurada: formatNumberAR(coverage.sumaCob),
              }));

              const possesionAux: Possession = {
                ubicacion: possession.ocupacion,
                localidadBien: possession.localidad,
                codigoPostalBien: possession.codPostal,
                tipoVivienda: possession.tipoVivienda,
                acreedor: possession.acreedor,
                clausulas: possession.clausulas.map(c => c.clausula).join(" - ") ?? '',
                coberturas: risksCoverage,                 
              };
              return possesionAux;
            });

            const request: CoverageConstancyServiceRequest = {
              rama: insurancePolicyDetail?.descripcionRama ?? '',
              poliza: insurancePolicyDetail?.nroPoliza!,
              vigenciaDesde: dayjs(insurancePolicyDetail!.fechaVigDesd!).format(FORMAT_DATE.AR),
              vigenciaHasta: dayjs(insurancePolicyDetail!.fechaVigHast).format(FORMAT_DATE.AR),
              fechaHoraReporte: dayjs().format(FORMAT_DATE.AR_HOUR_MIN),
              asegurado: insurancePolicyDetail?.nombreAsegurado ?? '',
              domicilio: (getValueByLabel(insuredsByPolicy.address, "Domicilio") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Localidad") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Provincia") ?? ''),
              productor: insurancePolicyDetail!.nombreProductor,
              productorCodigo: insurancePolicyDetail!.codigoProductor,
              endoso: endorsement?.nro,
              bienes: possessionsAux,
            }

            const response = await DocumentsService.getHomeCombinedFamilyDocument(request);
            downloadBase64Files(response.data.base64, documentToDownload, FILE_TYPES.PDF);
          } catch (error) {
          }

        } else if (document === DocumentType.ConstanciaCoberturaComercio) {
          try {
            const possessions: Ubication[] = possessionsList.filter(
              (possession: Possessions): possession is Ubication =>
                (possession as Ubication).coberturas && (possession as Ubication).coberturas !== undefined
            );

            const possessionsAux: Possession[] = possessions.map(possession => {
              const risksCoverage: Coverage[] = (possession.coberturas ?? []).map(coverage => ({
                coberturaNombre: coverage.nombreCob,
                moneda: endorsement?.moneda ?? '$',
                sumaAsegurada: formatNumberAR(coverage.sumaCob),
              }));

              const possesionAux: Possession = {
                ubicacion: possession.direccion,
                localidadBien: possession.localidad,
                codigoPostalBien: possession.codPostal,
                ocupacion: possession.ocupacion,
                actividad: possession.actividadComercial,
                clausulas: possession.clausulas.map(c => c.clausula).join(" - ") ?? '',
                coberturas: risksCoverage,                 
              };
              return possesionAux;
            });

            const request: CoverageConstancyServiceRequest = {
              rama: insurancePolicyDetail?.descripcionRama ?? '',
              poliza: insurancePolicyDetail?.nroPoliza!,
              vigenciaDesde: dayjs(insurancePolicyDetail!.fechaVigDesd!).format(FORMAT_DATE.AR),
              vigenciaHasta: dayjs(insurancePolicyDetail!.fechaVigHast).format(FORMAT_DATE.AR),
              fechaHoraReporte: dayjs().format(FORMAT_DATE.AR_HOUR_MIN),
              asegurado: insurancePolicyDetail?.nombreAsegurado ?? '',
              domicilio: (getValueByLabel(insuredsByPolicy.address, "Domicilio") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Localidad") ?? '') +', '+ (getValueByLabel(insuredsByPolicy.address, "Provincia") ?? ''),
              productor: insurancePolicyDetail!.nombreProductor,
              productorCodigo: insurancePolicyDetail!.codigoProductor,
              bienes: possessionsAux,
            }

            const response = await DocumentsService.getCommerceCoverageDocument(request);
           downloadBase64Files(response.data.base64, documentToDownload, FILE_TYPES.PDF); 
          } catch (error) {
          }
        } else if (document === DocumentType.TarjetaCirculacion) {
          const circulationCardData: CirculationCardData = {
            insurancePolicyDetail, endorsement, insuredsByPolicy,
            possessions: possessionsList
          }
          await getCirculationCardPDF(circulationCardData);

        } 
        else if (document === DocumentType.PolizaDigitalCompleta ||
          document === DocumentType.PolizaDigitalReducida ||
          document === DocumentType.FacturaAsegurado ||
          document === DocumentType.FacturaOrganizador ||
          document === DocumentType.FacturaProductor ||
          document === DocumentType.CuponeraDePago
        ) {
          // =========================
          // 1 - BUSCA DOCUMENTO EN S3 (PRESIGNED URL)
          // =========================
          const requestFilenet: EndorsementPolicyDocumentRequest = {
            documento: {
              nombreDocumento: documentToDownload,
              claseDocumental: DocumentClass.ElectronicPolicy,
              presigned: true
            }
          };

          try {
            const responseSearchFilenet =
            await DocumentsService.getEndorsementPolicyInPDF(requestFilenet);
            const presignedUrl = responseSearchFilenet.data.documento;
            if (presignedUrl) {
              await downloadPresignedFile(presignedUrl, documentToDownload, FILE_TYPES.PDF);
              return;
            }

          } catch (error) {
            // Continúa con generación on the fly
          }

          // =========================
          // 2 - GENERA DOCUMENTO ON THE FLY
          // =========================
          const requestOnTheFly: PolicyDocumentRequest = {
            empresa: auth.userInfo!.codigoDeEmpresa,
            sucursal: auth.userInfo!.codigoDeSucursal,
            articulo: insurancePolicyDetail!.articulo,
            superpoliza: insurancePolicyDetail!.superpoliza,
            suplementoSuperpoliza: endorsement!.nro,
            rama: insurancePolicyDetail!.rama,
            numeroPoliza: insurancePolicyDetail!.nroPoliza,
            suplementoPoliza: endorsement!.nro,
            documento: getPolicyDocumentType(document)
          };

          const response =
          await DocumentsService.getPolicyDocumentOnTheFly(requestOnTheFly);
          const base64 = response.data.base64;
          if (!base64) {
            throw new Error(
              '¡Ocurrió un error! No se pudo obtener ni generar el documento solicitado.'
            );
          }
          downloadBase64Files(base64, documentToDownload, FILE_TYPES.PDF);
        }
        else {
          const request: EndorsementPolicyDocumentRequest = {
            documento: {
              nombreDocumento: documentToDownload,
              claseDocumental: DocumentClass.ElectronicPolicy,
              presigned: true
            }
          };
          const response = await DocumentsService.getEndorsementPolicyInPDF(request);
          await downloadPresignedFile(response.data.documento, documentToDownload, FILE_TYPES.PDF);
        }
      }
    } catch (error) {
    } finally {
      setDownloading(false);
    }
  };

  const getPolicyDocumentType = (documentType: String): PolicyCreatorDocumentType => {
    switch (documentType) {
      case DocumentType.PolizaDigitalCompleta:
        return PolicyCreatorDocumentType.POLIZA_COMPLETA;
      case DocumentType.PolizaDigitalReducida:
        return PolicyCreatorDocumentType.POLIZA_REDUCIDA;
      case DocumentType.FacturaAsegurado:
        return PolicyCreatorDocumentType.POLIZA_FACTURAASEGURADO;
      case DocumentType.FacturaOrganizador:
        return PolicyCreatorDocumentType.POLIZA_FACTURAORGANIZADOR;        
      case DocumentType.FacturaProductor:
        return PolicyCreatorDocumentType.POLIZA_FACTURAPRODUCTOR;
      case DocumentType.TarjetaCirculacion:
        return PolicyCreatorDocumentType.POLIZA_TARJETACIRCULACION;
      case DocumentType.CuponeraDePago:
        return PolicyCreatorDocumentType.POLIZA_CUPONERA;
      default:
        return PolicyCreatorDocumentType.POLIZA_COMPLETA;
    }
  }
  
  const renderGrid = () => {
    if (isLoading) {
      return (
        <RowSize className='row-documents-tab-card'>
          <CardSkeletons />
        </RowSize>
      );
    }

    if (isEmpty) {
      return <PolicyNotFound />;
    }

    if (!requiresInsuredSelection(optionSelected) || allPersons.length === 0) {
      return null;
    }

    const { allSelected, someSelected, selectedCount } = insuredSelectionSummary;

    return (
      <>
        <RowSize
          className='row-documents-tab-insured-list'
          rowProps={{ gutter: [16, 16] }}
        >
          <ColSize size={100}>
            <div className='documents-tab-insured-selection-toolbar'>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(event) => handleSelectAllChange(event.target.checked)}
              >
                Seleccionar toda la nómina
              </Checkbox>
              <span className='documents-tab-insured-selection-summary'>
                {selectedCount === 0
                  ? 'Ningún asegurado seleccionado'
                  : selectedCount === allPersons.length
                    ? `Todos los asegurados seleccionados (${selectedCount})`
                    : `${selectedCount} de ${allPersons.length} asegurados seleccionados`}
              </span>
            </div>
            {paginatedInsuredCards.map((insured, index) =>
              _renderCard(`insured-card-${insuredListPage}-${index}`, insured)
            )}
          </ColSize>
        </RowSize>
        <RowSize
          rowProps={{ justify: 'end' }}
          className='row-documents-tab-all-insured-list'
        >
          <Pagination
            total={allPersons.length}
            current={insuredListPage}
            pageSize={insuredListPageSize}
            showTotal={(total: number) =>
              total === 1 ? 'Un asegurado' : `${total} asegurados`
            }
            onChange={handleInsuredListPageChange}
            showSizeChanger
          />
        </RowSize>
        <RowSize
          rowProps={{ justify: 'end' }}
          className='row-documents-tab-all-insured-list'
        >
          <ActionButton
            label='Solicitar'
            onClick={requestDocument}
            icon={<FileProtectOutlined />}
          />
        </RowSize>
      </>
    );
  };


  return (
    <div className='documents-tab-container'>
      {showError ? (
        <ErrorResults title='Hubo un error al intentar cargar los documentos de la póliza' />
      ) : (
        <RowSize rowProps={{ gutter: [0, 0] }}>
          <ColSize size={85}>
            <RowSize rowProps={{ gutter: [0, 0] }}>
              <Typography.Title level={ 4 } style={{ margin: 0, marginBottom: 10, marginTop: 20, color: COLORS.principal[100] }}
              >
                Solicitar certificados, constancias y/o póliza digital
              </Typography.Title>
            </RowSize>
            <RowSize rowProps={{ gutter: [0, 0] }}>
              <ColSize size={40} colProps={{ xs: { span: 18 }, sm: { span: 19 }, md: { span: 19 }, lg: { span: 9 } }}>
                <Select
                  placeholder={'Seleccione el documento que desea solicitar'}
                  options={listDocument}
                  onDeselect={(value) => handleOnChange(value)}
                  allowClear
                  onChange={(value, desc) => handleOnChange(value)}
                  style={{ color: COLORS.principal[100], width: '100%' }}
                  maxTagCount='responsive'
                  className='custom-select'
                  optionFilterProp='label'
                />
              </ColSize>
              { ((optionSelected === DocumentType.CertificadoCoberturaCalderas) || (optionSelected === DocumentType.CertificadoCoberturaAscensores)) && showDownloadButton && (
                <ColSize size={20} colProps={{ xs: { span: 18 }, sm: { span: 19 }, md: { span: 19 }, lg: { span: 9 } }}>

                  <InputNumberForm
                    formProps={{
                      label: 'Cantidad:',
                      name: 'quantity',
                      rules: validateForm.generateRules({
                        label: 'Cantidad',
                        rules: ['number', 'integer', 'required'],
                      }),
                    }}
                    inputNumberProps={{
                      max: 100,
                      min: 1,
                      onChange: (q => handleChangeQuanity(q)),
                      placeholder: 'Cantidad'
                    }}
                  />
                </ColSize>
              )}
              <ColSize size={40} colProps={{ xs: { span: 18 }, sm: { span: 19 }, md: { span: 19 }, lg: { span: 9 } }}>
                {showDownloadButton ? <ActionButton type="secondary"
                  className="btn-accent-outline-barbuss"
                  label="Descargar"
                  loading={downloading}
                  style={{ marginLeft: '1rem' }}
                  onClick={() => downloadEndorsementPolicyPDF()}
                  icon={<DownloadOutlined />} /> : ''}                  
              </ColSize>

              <ColSize size={75}  >
                  {(showARCAAlert) && (policeDocuments.includes(optionSelected)) && (
                    <><Alert style={{ marginTop: 15 }}
                      showIcon={true}
                      message={'No se permite obtener este documento. Pendiente de aprobación en ARCA.'}
                      type='error' />
                      <Alert style={{ marginTop: 15 }}
                        showIcon={true}
                        message={'Recuerde que de todas maneras se puede descargar la constancia de cobertura y el resto de los documentos.'}
                        type='info' />
                      </>                  
                  )}
              </ColSize>

            </RowSize>



            {optionSelected === DocumentType.ConstanciaCoberturaAPConClausula && !isEmpty &&  (
              <RowSize rowProps={{ gutter: [0, 0] }}>
                <ColSize size={100} colProps={{ xs: { span: 19 }, sm: { span: 17 }, md: { span: 17 }, lg: { span: 8 } }}>
                  <Form
                    form={formClause}
                    layout='vertical'
                    style={{ marginTop: '2rem' }}
                  >
                    <Form.Item
                      name='clause'
                      label='Cláusula'
                      rules={[
                        {
                          required: true,
                          message: 'Debe ingresar una cláusula.',
                        },
                        {
                          validator: (_, value) => {
                            if (value && value.trimLeft() !== value) {
                              const message =
                                'No se permite un espacio al principio.';
                              return Promise.reject(message);
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder='Cláusula'
                        maxLength={7000}
                        showCount
                      />
                    </Form.Item>
                  </Form>
                </ColSize>
              </RowSize>
            )}
            {optionSelected === DocumentType.ConstanciaCoberturaAPBeneficiarioPrimerTermino && !isEmpty &&
          <div className='container-clauses'>
              <RowSize rowProps={{ gutter: [0, 0] }}>
                
                <ColSize size={100} colProps={{ xs: { span: 19 }, sm: { span: 17 }, md: { span: 17 }, lg: { span: 8 } }}>
                  <Form
                    form={formClause}
                    layout='vertical'
                    style={{ marginTop: '2rem' }}
                  >
                    <Form.Item
                      name='clause'
                      label='Cláusula de no repetición'
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value && value.trimLeft() !== value) {
                              const message =
                                'No se permite un espacio al principio.';
                              return Promise.reject(message);
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder='Cláusula de no repetición'
                        maxLength={7000}
                        showCount
                      />
                    </Form.Item>
                  </Form>
                </ColSize>
              </RowSize>

            <RowSize rowProps={{ gutter: [0, 0] }}>
                <ColSize size={100} colProps={{ xs: { span: 19 }, sm: { span: 17 }, md: { span: 17 }, lg: { span: 8 } }}>
                  <Form
                    form={formClause}
                    layout='vertical'
                    style={{ marginTop: '2rem' }}
                  >
                    <Form.Item
                      name='beneficiaryClause'
                      label="Cláusula de beneficiario en primer término"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value && value.trimLeft() !== value) {
                              const message =
                                'No se permite un espacio al principio.';
                              return Promise.reject(message);
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder='Cláusula de beneficiario en primer término'
                        maxLength={7000}
                        showCount
                      />
                    </Form.Item>
                  </Form>
                </ColSize>
              </RowSize>
</div>}

            {renderGrid()}

          </ColSize>

          <ColSize size={9} colProps={{ id: 'documents-tab-container-anchor' }} >
            <Anchor
              items={MOCK_SECTIONS}
              containerId='documents-tab-container-anchor'
            />
          </ColSize>
        </RowSize>
      )}
    </div>
  );
};